import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook } from "lucide-react";
import { http, BRAND } from "../lib/api";
import { toast } from "sonner";
import { openCookieSettings } from "./CookieBanner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await http.post("/newsletter", { email });
      toast.success(data.message || "Mulțumim!");
      setEmail("");
    } catch (err) {
      toast.error("Hmm, ceva nu a mers. Încearcă din nou.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="mt-24 relative" style={{ background: "var(--vdz-navy)", color: "var(--vdz-cream)" }} data-testid="site-footer">
      <div className="container-vdz py-16 md:py-20">
        {/* Newsletter */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <div className="eyebrow">Newsletter</div>
          <h3 className="font-serif-display text-3xl md:text-4xl mt-3 mb-3">Pentru visătoarele frumoase.</h3>
          <p className="text-sm md:text-base opacity-80 mb-6 max-w-md mx-auto">
            Primește noutăți despre ediții sezoniere, inspirație pentru colorat și povești din atelierul Vis de Zi.
          </p>
          <form onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adresa.ta@email.com"
              className="flex-1 px-4 py-3 bg-transparent border text-cream placeholder:text-cream/50 focus:outline-none"
              style={{ borderColor: "rgba(201,162,74,0.4)", color: "var(--vdz-cream)" }}
              data-testid="newsletter-email-input"
            />
            <button type="submit" disabled={loading} className="btn-gold" data-testid="newsletter-submit-button">
              {loading ? "Trimit..." : "Mă înscriu"}
            </button>
          </form>
        </div>

        <div className="gold-divider mb-14"><span className="eyebrow">VIS DE ZI</span></div>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="font-serif-display text-2xl mb-3">VIS DE ZI</div>
            <p className="text-xs tracking-[0.22em] uppercase mb-4 opacity-70 font-serif-accent">Colorează. Respiră. Visează.</p>
            <p className="text-sm opacity-70 leading-relaxed">
              Brand românesc de cărți de colorat pentru adulți, creat cu drag de <span className="gold-text font-serif-display text-base">Alina Stancu</span>.
            </p>
          </div>
          <div>
            <h4 className="font-serif-accent uppercase tracking-[0.22em] text-xs mb-5 gold-text">Magazin</h4>
            <ul className="space-y-2.5 text-sm opacity-80">
              <li><Link to="/carti" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-carti">Cărți</Link></li>
              <li><Link to="/colectii" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-colectii">Colecții</Link></li>
              <li><Link to="/jurnal" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-jurnal">Jurnal</Link></li>
              <li><Link to="/galerie" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-galerie">Galerie</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif-accent uppercase tracking-[0.22em] text-xs mb-5 gold-text">Informații</h4>
            <ul className="space-y-2.5 text-sm opacity-80">
              <li><Link to="/despre" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-despre">Despre noi</Link></li>
              <li><Link to="/contact" className="hover:text-[color:var(--vdz-gold-pale)]" data-testid="footer-link-contact">Contact</Link></li>
              <li><Link to="/livrare" className="hover:text-[color:var(--vdz-gold-pale)]">Livrare</Link></li>
              <li><Link to="/termeni" className="hover:text-[color:var(--vdz-gold-pale)]">Termeni și condiții</Link></li>
              <li><Link to="/confidentialitate" className="hover:text-[color:var(--vdz-gold-pale)]">Politica de confidențialitate</Link></li>
              <li><Link to="/cookies" className="hover:text-[color:var(--vdz-gold-pale)]">Politica de cookies</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif-accent uppercase tracking-[0.22em] text-xs mb-5 gold-text">Rămâi aproape</h4>
            <div className="flex items-center gap-4">
              <a href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center border transition-all hover:border-[color:var(--vdz-gold)] hover:text-[color:var(--vdz-gold)]" style={{ borderColor: "rgba(201,162,74,0.35)" }} data-testid="footer-social-instagram">
                <Instagram size={18} strokeWidth={1.4} />
              </a>
              <a href={BRAND.social.tiktok} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center border transition-all hover:border-[color:var(--vdz-gold)] hover:text-[color:var(--vdz-gold)]" style={{ borderColor: "rgba(201,162,74,0.35)" }} data-testid="footer-social-tiktok">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.321 5.562a5.124 5.124 0 0 1-5.16-4.956L10.993.6v12.616c0 1.512-1.224 2.736-2.736 2.736s-2.736-1.224-2.736-2.736 1.224-2.736 2.736-2.736c.312 0 .6.048.864.144V7.464a6.058 6.058 0 0 0-.864-.072c-3.264 0-5.904 2.64-5.904 5.904S4.993 19.2 8.257 19.2s5.904-2.64 5.904-5.904v-6.6a8.238 8.238 0 0 0 5.16 1.776V5.562z"/></svg>
              </a>
              <a href={BRAND.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 flex items-center justify-center border transition-all hover:border-[color:var(--vdz-gold)] hover:text-[color:var(--vdz-gold)]" style={{ borderColor: "rgba(201,162,74,0.35)" }} data-testid="footer-social-facebook">
                <Facebook size={18} strokeWidth={1.4} />
              </a>
            </div>
            <p className="mt-6 text-xs opacity-60 leading-relaxed">
              Livrare în toată România prin curier.<br />
              Plată la ramburs disponibilă.
            </p>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t text-center text-xs opacity-60" style={{ borderColor: "rgba(201,162,74,0.2)" }}>
          <div className="mb-3">
            <button
              onClick={openCookieSettings}
              className="hover:text-[color:var(--vdz-gold-pale)] underline-offset-4 hover:underline transition-colors"
              data-testid="footer-cookie-settings-button"
            >
              Setări cookies
            </button>
          </div>
          © {new Date().getFullYear()} VIS DE ZI · Creat cu drag în România de Alina Stancu.
        </div>
      </div>
    </footer>
  );
}
